<script setup>
import { computed, nextTick, ref } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import Validator from 'devextreme/ui/validator'
import * as dataGrid from '@/composables/data-grid'
import { useColumns } from '@/composables/registry/product-stock-grid'
import { getI18n } from '@/plugins/i18n'
import { useRegistryStore } from '@/stores/registry.store'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import DocumentRowStockInfo from '@/components/shared/DocumentRowStockInfo.vue'

const props = defineProps({
  product: { type: Object, required: true },
  entityLock: { type: Object, required: true },
})

const i18n = getI18n()
const registryStore = useRegistryStore()
const { gridRef } = dataGrid.useGridRef()

const stockEditors = {}
const currentStock = ref({})
const stockInfo = computed(() => ({
  ...currentStock.value,
  customerCommitted: currentStock.value.customer_committed,
  workshopCommitted: currentStock.value.workshop_committed,
  theoreticalStock: Number(currentStock.value.stock ?? 0) + Number(currentStock.value.ordered ?? 0) - Number(currentStock.value.customer_committed ?? 0) - Number(currentStock.value.workshop_committed ?? 0),
  averageCost: currentStock.value.average_cost,
  weightedAverageCost: currentStock.value.weighted_average_cost,
  lastCost: currentStock.value.last_cost,
}))

const columns = useColumns()
const canAddStock = computed(() => !props.entityLock.isLocked(false, props.product.id))
const gridDataSource = computed(() => {
  const product = props.product.id
  return dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productStocksUrl, {
    customParamsFn: () => ({ $notop: true, product_ref: product }),
    avoidDuplicateDatasourceIds: true,
  })
})
const rowButtons = props.entityLock.getEntityLockButtons({
  visible: {
    delete: e => e.data?.has_movements === false && !props.entityLock.isLocked(false, props.product.id),
  },
})

const editing = dataGrid.useDefaultEditing(() => `${i18n.t('common.stock')} · ${props.product.code}`, {
  colCount: 2,
  items: [
    {
      itemType: 'group', caption: i18n.t('common.stock'), colCount: 2,
      items: [
        { dataField: 'deposit', colSpan: 2 },
        { dataField: 'position', colSpan: 2 },
        'minimum_stock', 'maximum_stock',
      ],
    },
    {
      itemType: 'group', caption: i18n.t('common.info', 2), colCount: 2,
      items: [
        { template: 'stockInformationTemplate', colSpan: 2 },
        'last_zero_stock_date', 'last_movement_date',
      ],
    },
  ],
}, {
  allowAdding: true,
  allowDeleting: e => e.row?.data?.has_movements === false && !props.entityLock.isLocked(false, props.product.id),
  allowUpdating: () => !props.entityLock.isLocked(false, props.product.id),
  entityLock: props.entityLock,
  onInitializedCustom: e => props.entityLock.readOnlyToolbarItems(e),
})

const onInitNewRow = (e) => {
  if (!canAddStock.value) {
    e.component.cancelEditData()
    return
  }
  e.data.product_ref = props.product.id
  e.data.position = ''
  for (const field of ['minimum_stock', 'maximum_stock', 'stock', 'ordered', 'customer_committed', 'workshop_committed', 'average_cost', 'weighted_average_cost', 'last_cost']) {
    e.data[field] = 0
  }
  currentStock.value = e.data
}

const onEditingStart = (e) => { currentStock.value = e.data }

const onEditorPreparing = (e) => {
  if (e.parentType !== 'dataRow') return
  if (e.dataField === 'deposit') e.editorOptions.readOnly = !e.row.isNewRow
  const otherField = { minimum_stock: 'maximum_stock', maximum_stock: 'minimum_stock' }[e.dataField]
  if (!otherField) return
  const onInitialized = e.editorOptions.onInitialized
  e.editorOptions.onInitialized = args => {
    onInitialized?.(args)
    stockEditors[e.dataField] = args.component
  }
  const onDisposing = e.editorOptions.onDisposing
  e.editorOptions.onDisposing = args => {
    onDisposing?.(args)
    if (stockEditors[e.dataField] === args.component) delete stockEditors[e.dataField]
  }
  const onValueChanged = e.editorOptions.onValueChanged
  e.editorOptions.onValueChanged = async args => {
    onValueChanged(args)
    await nextTick()
    const editor = stockEditors[otherField]
    if (editor) Validator.getInstance(editor.element())?.validate()
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: 'product-stock-grid',
  columns,
  editing,
  entityLock: props.entityLock,
  entityLockId: () => props.product.id,
  mode: 'row',
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow,
  onEditingStart,
  onEditorPreparing,
  onToolbarPreparing: e => dataGrid.useDefaultToolbar(e, {}),
  height: '100%',
})
</script>

<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
    :data-source="gridDataSource"
    :editing="{ ...editing, allowAdding: canAddStock }"
  >
    <template #stockInformationTemplate>
      <document-row-stock-info
        :data="stockInfo"
        :show-settings="false"
        :show-current-cost="false"
      />
    </template>
    <template #customToolbarAddButtonTemplate>
      <custom-toolbar-add-button-template
        :master-entity-id="props.product.id"
        :grid="gridRef"
        :entity-lock="props.entityLock"
      />
    </template>
    <template #editButtonsTemplate="{ data: cellInfo }">
      <edit-lock-buttons-template
        entity-name="Product"
        :master-entity-id="props.product.id"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :buttons="rowButtons"
      />
    </template>
  </dx-data-grid>
</template>
